import { useState, useCallback } from "react";
import {
  Page,
  Button,
  ConfirmDialog,
  EmptyState,
  Badge,
  ItemTable,
  ItemTableSearch,
  ItemTableBody,
  ItemTableRow,
  ItemTableCell,
  ItemTableAvatar,
  ItemTableTitle,
  ItemTableSubtitle,
  ItemTableActions,
  ItemTablePagination,
  ItemTableLoading,
  ItemTableEmpty,
  type BadgeColor,
} from "@ui";
import {
  FolderOpen,
  Upload,
  FileText,
  Image,
  Table,
  File,
  Trash2,
  Download,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import {
  useDocuments,
  useDeleteDocument,
  useSearchDocuments,
  type Document,
  formatFileSize,
} from "@agentic_system/api/document";
import { useDocumentsSocket } from "@agentic_system/hooks";
import { UploadDialog } from "./components/upload-dialog";
import { DocumentDrawer } from "./components/document-drawer";

const FILE_ICONS: Record<string, LucideIcon> = {
  image: Image,
  pdf: FileText,
  word: FileText,
  excel: Table,
  default: File,
};

const STATUS_CONFIG: Record<
  Document["ocr_status"],
  { label: string; color: BadgeColor; icon?: LucideIcon }
> = {
  pending: { label: "En attente", color: "default" },
  processing: { label: "En cours", color: "blue", icon: Loader2 },
  completed: { label: "Prêt", color: "green" },
  failed: { label: "Échoué", color: "red" },
  skipped: { label: "Ignoré", color: "yellow" },
};

function getFileIcon(contentType: string): LucideIcon {
  if (contentType.startsWith("image/")) return FILE_ICONS.image;
  if (contentType === "application/pdf") return FILE_ICONS.pdf;
  if (contentType.includes("wordprocessingml")) return FILE_ICONS.word;
  if (contentType.includes("spreadsheetml")) return FILE_ICONS.excel;
  return FILE_ICONS.default;
}

function getDownloadUrl(docId: number): string {
  return `/system/agentic/documents/${docId}/download`;
}

export function Files() {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );

  // Subscribe to real-time document updates (OCR/embedding status changes)
  useDocumentsSocket();

  const isSearching = searchQuery.length >= 2;
  const {
    data: listData,
    isLoading: listLoading,
    error: listError,
  } = useDocuments(page);
  const { data: searchData, isLoading: searchLoading } = useSearchDocuments(
    searchQuery,
    20,
  );

  const deleteMutation = useDeleteDocument();

  const openUploadDialog = useCallback(() => {
    setUploadOpen(true);
  }, []);

  const handleDelete = useCallback((doc: Document) => {
    setDeleteTarget(doc);
  }, []);

  const handleSelect = useCallback((doc: Document) => {
    setSelectedDocumentId(doc.id);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (deleteTarget) {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteMutation]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    if (value.length >= 2) {
      setPage(1);
    }
  }, []);

  const error = isSearching ? null : listError;
  const documents = isSearching
    ? (searchData?.documents ?? [])
    : (listData?.documents ?? []);
  const meta = isSearching ? null : listData?.meta;
  const isSearchLoadingWithNoData = isSearching && searchLoading && !searchData;

  if (error) {
    return (
      <Page
        data-testid="files"
        title="Fichiers"
        description="Téléchargez des fichiers que les agents peuvent utiliser"
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Impossible de charger les documents. Veuillez réessayer.
        </div>
      </Page>
    );
  }

  const hasDocuments = (listData?.documents?.length ?? 0) > 0;
  const hasResults = documents.length > 0;
  const showLoading = listLoading && !isSearching;

  return (
    <Page
      data-testid="files"
      title="Fichiers"
      description="Téléchargez des fichiers que les agents peuvent utiliser"
      actions={
        <Button icon={Upload} onClick={openUploadDialog}>
          Télécharger des fichiers
        </Button>
      }
    >
      {showLoading ? (
        <ItemTable>
          <ItemTableLoading rows={5} />
        </ItemTable>
      ) : hasDocuments ? (
        <ItemTable>
          <ItemTableSearch
            value={searchQuery}
            onChange={handleSearchChange}
            isLoading={searchLoading && isSearching}
            placeholder="Rechercher des documents par contenu..."
          />
          <ItemTableBody>
            {isSearchLoadingWithNoData ? (
              <ItemTableLoading rows={3} />
            ) : hasResults ? (
              documents.map((doc) => {
                const Icon = getFileIcon(doc.content_type);
                const isImage = doc.content_type.startsWith("image/");
                const statusConfig = STATUS_CONFIG[doc.ocr_status];

                return (
                  <ItemTableRow
                    key={doc.id}
                    onClick={() => handleSelect(doc)}
                    className="grid-cols-[1fr_auto_auto_auto] gap-x-4"
                  >
                    <ItemTableCell>
                      <ItemTableAvatar
                        icon={isImage ? undefined : Icon}
                        src={isImage ? doc.thumbnail_url : undefined}
                        alt={doc.filename}
                      />
                      <div className="min-w-0">
                        <ItemTableTitle>{doc.filename}</ItemTableTitle>
                        <ItemTableSubtitle>
                          {doc.content_type}
                        </ItemTableSubtitle>
                      </div>
                    </ItemTableCell>
                    <ItemTableCell>
                      <Badge
                        label={statusConfig.label}
                        color={statusConfig.color}
                        iconLeft={statusConfig.icon}
                        size="sm"
                      />
                    </ItemTableCell>
                    <ItemTableCell className="text-sm text-slate-500">
                      {formatFileSize(doc.byte_size)}
                    </ItemTableCell>
                    <ItemTableCell>
                      <ItemTableActions
                        items={[
                          {
                            label: "Télécharger",
                            icon: <Download className="size-4" />,
                            onClick: () => {
                              window.location.href = getDownloadUrl(doc.id);
                            },
                          },
                          {
                            label: "Supprimer",
                            icon: <Trash2 className="size-4" />,
                            onClick: () => handleDelete(doc),
                            variant: "danger",
                          },
                        ]}
                      />
                    </ItemTableCell>
                  </ItemTableRow>
                );
              })
            ) : isSearching ? (
              <ItemTableEmpty
                title="Aucun document trouvé"
                description={`Aucun document ne correspond à "${searchQuery}"`}
              />
            ) : null}
          </ItemTableBody>
          {!isSearching && meta && meta.total_pages > 1 && (
            <ItemTablePagination meta={meta} onPageChange={setPage} />
          )}
        </ItemTable>
      ) : (
        <EmptyState
          icon={FolderOpen}
          title="Aucun fichier téléchargé"
          description="Téléchargez des documents, images ou autres fichiers que vos agents peuvent utiliser."
          action={
            <Button icon={Upload} onClick={openUploadDialog}>
              Télécharger des fichiers
            </Button>
          }
        />
      )}

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />

      <DocumentDrawer
        documentId={selectedDocumentId}
        onClose={() => setSelectedDocumentId(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Supprimer le fichier"
        subtitle={deleteTarget?.filename}
        description="Êtes-vous sûr de vouloir supprimer ce fichier ? Cette action est irréversible."
        confirm={{
          label: "Supprimer",
          onClick: confirmDelete,
        }}
        variant="destructive"
        loading={deleteMutation.isPending}
      />
    </Page>
  );
}
